import React, { type ReactNode, useEffect, useState } from "react";
import * as Sentry from "@sentry/nextjs";
import { stagger, useAnimate, useInView } from "framer-motion";
import { AspectRatio, Box } from "@chakra-ui/react";
import { BookDemoButton } from "components/event-tracking";
import { Section, sectionSpacingY } from "components/section";
import { FillImage } from "components/images";
import { FadeIn } from "ui/animations";
import { TextTitle } from "ui/text";
import { ChakraBox } from "ui/chakra-framer";
import mobileImage from "../../../../../public/assets/platform/composable-cdp/mobile-hero.png";
import { UseCaseCards } from "./cards";
import { AuraAnimation } from "./aura";

const auraDuration = 0.9;
const sharedAuraAnimation = { translateZ: ["1px", "1px"], translateY: ["-50%", "-50%"] };
const sharedAuraTransition = { at: "-0.2", duration: auraDuration };

function useCdpHeroAnimation(isActive: boolean) {
  const [scope, animate] = useAnimate();

  useEffect(() => {
    const triggerAnimation = async () => {
      if (isActive) {
        try {
          await animate([
            [
              "#aura-1",
              { opacity: [0, 1], scale: [0.5, 1], ...sharedAuraAnimation },
              { delay: 0.15, duration: auraDuration },
            ],
            ["#aura-2", { opacity: [0, 0.75], scale: [1, 1.7], ...sharedAuraAnimation }, { ...sharedAuraTransition }],
            ["#aura-3", { opacity: [0, 0.5], scale: [1.7, 2.5], ...sharedAuraAnimation }, { ...sharedAuraTransition }],
            ["#aura-4", { opacity: [0, 0.25], scale: [2.5, 3.3], ...sharedAuraAnimation }, { ...sharedAuraTransition }],
            [
              ".card",
              { opacity: [0, 1], scale: [0.9, 1] },
              { at: "-2", type: "spring", stiffness: 400, damping: 30, delay: stagger(0.35) },
            ],
          ]);
        } catch (error) {
          Sentry.captureException(error);
        }
      }
    };

    // Do not need `await` because there is no action after the promise
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    triggerAnimation();
  }, [isActive, animate]);

  return scope;
}

type CdpHeroProps = {
  eyebrow?: string;
  title: string;
  description: ReactNode;
};

export function CdpHero({ eyebrow, title, description }: CdpHeroProps) {
  const [animationActive, setAnimationActive] = useState(false);
  const scope = useCdpHeroAnimation(animationActive);
  const isInView = useInView(scope, { amount: 0.5, once: true });

  useEffect(() => {
    setAnimationActive(isInView);
  }, [isInView]);

  return (
    <Box ref={scope} bg="new.green.950" pos="relative" overflow="hidden">
      <ChakraBox
        display={{ base: "none", lg: "block" }}
        pos="absolute"
        inset={0}
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1, transition: { type: "easeInOut", duration: 0.3 } }}
        viewport={{ once: true }}
      >
        <AuraAnimation />
        <UseCaseCards />
      </ChakraBox>

      <Section theme="dark" themeBgColor="new.green.950" pos="relative" overflow="hidden" my={0}>
        <FadeIn duration={0.3}>
          <TextTitle
            theme="dark"
            size="xl"
            chip={eyebrow ? { text: eyebrow } : undefined}
            titleAs="h1"
            title={title}
            descriptionAs="h2"
            description={description}
            actions={<BookDemoButton />}
            maxW={{ base: "100%", lg: "50%" }}
            pt={sectionSpacingY}
            pb={{ base: 0, lg: "sectionSpacingY.md" }}
          />

          <AspectRatio
            display={{ base: "block", lg: "none" }}
            ratio={1 / 1}
            pos="relative"
            w="100%"
            maxW="640px"
            mx="auto"
            mt="small"
            mb={{ base: "small", lg: 0 }}
          >
            <FillImage
              priority
              src={mobileImage}
              alt="Abstract illustration of marketing cases for the Composable CDP including Conversion uploads, Profile enrichment, Email personalization, Audience targeting, Audience suppression, and Journey orchestration."
            />
          </AspectRatio>
        </FadeIn>
      </Section>
    </Box>
  );
}
